import { finalize, tap } from 'rxjs/operators';
import { CheckboxFilterComponent } from '@/shared/components/table/components/checkbox-filter/checkbox-filter.component';
import { SearchbarComponent } from '@/shared/components/table/components/searchbar/searchbar.component';
import { FilterComponent } from '@/shared/components/table/components/filter/filter.component';
import { MatSort, Sort } from '@angular/material/sort';
import { PaginatorComponent } from '@/shared/components/table/components/paginator/paginator.component';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ClusterFragment, ClusterListFilters, ClustersGQL, ClusterSortField, SortDefinitionInput, SortDirection } from "@/core/graphql/graphql";
import { PaginationMetadata, TableDataSource } from "@/shared/components/table/table.model";
import { CollectionViewer } from '@angular/cdk/collections';
import { CLUSTER_SORT_FIELD } from '@/modules/clusters/clusters.const';

const DEFAULT_PAGINATION_FACTORY = () => ({
  previousCursors: [],
  currentCursor: null,
  nextCursor: null,
  pageSize: 20,
  count: null,
});

export class ClusterDataSource extends TableDataSource<ClusterFragment> {
  public readonly _loading$: Observable<boolean>;
  protected _paginator: PaginatorComponent;
  protected _sort: MatSort;
  protected _searchbar: SearchbarComponent;
  private _currentPagination: PaginationMetadata = DEFAULT_PAGINATION_FACTORY();
  private _sortDefinition: SortDefinitionInput = {
    sortField: ClusterSortField.UpdatedAt,
    sortDirection: SortDirection.Desc,
  };
  private _filters: ClusterListFilters = {
    searchTerm: '',
  };
  private readonly _clustersSubject = new BehaviorSubject<ClusterFragment[]>([]);
  private readonly _loadingSubject = new BehaviorSubject<boolean>(true);
  private readonly _dataChangedSubject = new Subject<void>();
  private readonly _paginationMetaSubject = new BehaviorSubject<PaginationMetadata>(this._currentPagination);

  public constructor (
    private readonly _clustersGql: ClustersGQL,
  ) {
    super();
    this._loading$ = this._loadingSubject.asObservable();
    this.dataChanged$ = this._dataChangedSubject.asObservable();
  }

  public set paginator(component: PaginatorComponent) {
    this._paginator = component;
    this._paginator.fetchedPageCursor$.subscribe(this.loadData.bind(this));
  }

  public set sort(direction: MatSort) {
    this._sort = direction;
    this._sort.sortChange.subscribe(this.onSortChange.bind(this));
  }

  public set filter(component: FilterComponent) {
    throw new Error('Method not implemented');
  }

  public set searchbar(component: SearchbarComponent) {
    this._searchbar = component;
    this._searchbar.appliedSearchTerm$.subscribe((x) => this.onSearchSubmission(x));
  }

  public set checkboxFilter(component: CheckboxFilterComponent) {
    throw new Error('Method not implemented');
  }

  public connect(_: CollectionViewer): Observable<readonly ClusterFragment[]> {
    return this._clustersSubject.asObservable().pipe(
      tap(_ => this._dataChangedSubject.next()),
    );
  }

  public disconnect(_: CollectionViewer): void {
    this._clustersSubject.complete();
    this._loadingSubject.complete();
  }

  public loadData(currentPageCursor: string | null | undefined): void {
    this._loadingSubject.next(true);
    this._clustersGql
      .fetch({
        filters: this._filters,
        meta: {
          pageSize: this._currentPagination.pageSize,
          pageCursor: currentPageCursor,
        },
        sort: [this._sortDefinition],
      }).pipe(
        finalize(() => this._loadingSubject.next(false)),
      ).subscribe((x) => {
        this._clustersSubject.next(x.data.clusters.items);
        this._currentPagination.nextCursor = x.data.clusters.meta.nextPageCursor;
        this._currentPagination.count = x.data.clusters.meta.count;
        this._paginationMetaSubject.next(this._currentPagination);
        this._paginator.getDatasourceMetaData(this._paginationMetaSubject);
      });
  }

  public onSortChange({ active, direction }: Sort): void {
    if (direction) {
      this._sortDefinition = {
        sortField: CLUSTER_SORT_FIELD(active),
        sortDirection: direction === 'asc' ? SortDirection.Asc : SortDirection.Desc,
      };
    } else {
      this._sortDefinition = {
        sortField: ClusterSortField.UpdatedAt,
        sortDirection: SortDirection.Desc,
      };
    }
    this.resetDatasourcePagination();
  }

  public onSearchSubmission(search: string): void {
    if (search.length > 0) {
      this._filters.searchTerm = search;
    } else {
      this._filters.searchTerm = undefined;
    }
    this.resetDatasourcePagination();
  }

  private resetDatasourcePagination(): void {
    this._currentPagination = DEFAULT_PAGINATION_FACTORY();
    this._paginationMetaSubject.next(this._currentPagination);
    this._paginator.resetPaginator();
    this.loadData(null);
  }
}
